<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="element-wrapper">
        <BackButton
            :label="$t('general.button.back')"
            :to="{ name: 'billingCoachingSelection' }"
            marginBottom="5"/>

        <h6 class="element-header">
          Rechnungslegung
        </h6>
        <div class="element-box">
          <div class="row">
            <div class="col-6">
              <h5 class="form-header mb-4">
                Betreutes Wohnen - Rechnungslegung
              </h5>
            </div>
          </div>

          <Search
              :filters="filters"
              :loading="isSearching"
              :officeDropdown="officeDropdown"
              :billingTypeDropdown="billingTypeDropdown"
              @search="previewList"/>

          <form @submit.prevent="" method="post">
            <div class="table-responsive">
              <table id="dataTable" class="table table-striped">
                <thead>
                <tr>
                  <th><input type="checkbox" v-model="selectAll" @change="toggleAll" class="custom-checkbox-size"/></th>
                  <th>
                    <div class="btn-link" @click.prevent="sortBy('client')">
                      <span>Kostenträger</span>
                      <i :class="getIconClass('client')"></i>
                    </div>
                  </th>
                  <th>Kundennummer</th>
                  <th>Kundenname</th>
                  <th>Maßnahmentyp</th>
                  <th style="text-align: right">Sitzungen</th>
                  <th style="text-align: right">Rechnungsbetrag</th>
                  <th></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="coaching in coachingList" :key="coaching.id">
                  <td class="text-center">
                    <input
                        type="checkbox"
                        v-model="selectedCoachingIds"
                        :value="coaching.id"
                        class="custom-checkbox-size"/>
                  </td>
<!--                  <td><input type="checkbox" v-model="selectedCoachingIds" :value="coaching.id"/></td>-->
                  <td>{{ coaching?.client }}</td>
                  <td>{{ coaching?.unemployedInsuranceNumber }}</td>
                  <td>{{ coaching?.unemployedFullName }}</td>
                  <td>{{ coaching?.operationType }}</td>
                  <td style="text-align: right">{{ coaching?.units }}</td>
                  <td style="text-align: right">{{ coaching?.sum }}</td>
                  <td class="text-end ps-5">
                    <button v-if="coaching.billingType === 'individual_private_payment'"
                            @click="downloadFile('/billing/assisted-living-coaching/individual-bill-preview',
                       undefined,
                       {...filters, id: coaching?.id})"
                            class="btn btn-primary">Vorschau
                    </button>

                    <button v-if="coaching.billingType === 'flat_rate_only_final_payment'"
                            @click="downloadFile(`/billing/assisted-living-coaching/flat-rate-bill-preview/${coaching?.id}`)"
                            class="btn btn-primary">Vorschau
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <div class="mt-5 me-1 text-center"
                 v-if="selectedCoachingIds.length > 0"
                 @click="createBill">
              <button type="submit" class="btn btn-primary">Rechnung erstellen</button>
            </div>
          </form>
        </div>
      </div>

      <ModalConfirm
          v-model="showCreateBillConfirm"
          confirmationQuestion="Sind Sie sicher, dass Sie eine Rechnung erstellen möchten?"
          :header="approveHeader"
          :onConfirm="confirmCreateBill"/>
    </div>
  </PageTemplate>
</template>

<script setup>
import billingService from '@/api/Billing';
import BackButton from '@/components/button/BackButton.vue';
import Loader from '@/components/Loader.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {getOfficeDropdown} from '@/shared/dropdowns';
import {downloadFile, getSortIconClass} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import Search from '@/views/App/Billing/Creating/AssistedLivingCoaching/Search.vue';
import {computed, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();
const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const selectAll = ref(false);
const selectedCoachingIds = ref([]);
const officeDropdown = ref([]);
const billingTypeDropdown = ref([]);
const coachingList = ref([]);
const approveHeader = ref('');
const isSearching = ref(false);
const isLoaded = ref(false);
const showCreateBillConfirm = ref(false);

const filters = ref({
  billingType: 'individual_private_payment',
  courseId: '',
  clientId: '',
  locationId: '',
  officeId: '',
  startDate: '',
  endDate: '',
  signed: false,
});

async function previewList(passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  try {
    selectAll.value = false;
    selectedCoachingIds.value = [];

    const {data} = await billingService.getAssistedLivingCoachingPreviewList({
      ...(passedFilters ? passedFilters : filters.value),
    });

    coachingList.value = data.data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function getBillingTypeEnum() {
  // billingTypeDropdown.value = getEnums?.value?.assistedLivingCoachingBillingTypes || [];
  billingTypeDropdown.value = [
    {
      label: "Individuelle Privatzahlung",
      value: "individual_private_payment"
    }
  ]
}

function toggleAll() {
  if (selectAll.value) {
    selectedCoachingIds.value = coachingList.value.map(coaching => coaching.id);
  } else {
    selectedCoachingIds.value = [];
  }
}

watch(selectedCoachingIds, (newValue) => {
  // If all checkboxes are selected, check the header checkbox
  if (newValue.length > 0) {
    selectAll.value = newValue.length === coachingList.value.length;
  }
});

function createBill() {
  showCreateBillConfirm.value = true;
}

async function confirmCreateBill() {
  try {
    await billingService.createAssistedLivingCoachingBill({
      ...filters.value,
      coachingIds: selectedCoachingIds.value,
    });

    showCreateBillConfirm.value = false;
    await router.push({name: 'billingLatestBills'});
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    showCreateBillConfirm.value = false;
    await ErrorToast(error);
  }
}

function getIconClass(key) {
  return getSortIconClass(filters.value, key);
}

async function sortBy(key) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy: filters.value.orderBy === 'DESC' || filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };

  await previewList();
}


onMounted(async () => {
  await Promise.all([
    getOfficeDropdown(officeDropdown),
    getBillingTypeEnum(),
  ]);

  isLoaded.value = true;
});
</script>

